import { Marker, Popup, MapRef } from "react-map-gl/mapbox"
import { useState, RefObject, useMemo } from "react"
import { Avatar, Tag } from "antd"
import { MarkerStatus } from "./ScheduleMapMarker"
import { getInitials } from "../scheduleModel"
import dayjs from "dayjs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrafficCone } from '@fortawesome/pro-solid-svg-icons'
import { CheckCircleOutlined, RightOutlined, DownOutlined } from "@ant-design/icons"
import { ProjectStatuses } from "../../../common/constants"

export type ClusteredEventWorkArea = {
  id: number
  name: string
  status: number | null
}

export type ClusteredEvent = {
  id: number
  status: MarkerStatus
  crewLeadAvatar?: string | null
  crewLeadName?: string | null
  crewLeadColor?: string | null
  eventName: string
  customerDisplayName?: string | null
  start: string
  end: string
  workAreas: ClusteredEventWorkArea[]
}

export type Props = {
  latitude: number
  longitude: number
  events: ClusteredEvent[]
  address?: string
  isHovered?: boolean
  showPopupOnHover?: boolean
  mapRef?: RefObject<MapRef | null>
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

const DEFAULT_CLUSTER_COLOR = '#FF5C35'

type StatusGroupProps = {
  title: string
  events: ClusteredEvent[]
  defaultExpanded: boolean
  renderEvent: (event: ClusteredEvent) => React.ReactNode
}

const StatusGroup = ({ title, events, defaultExpanded, renderEvent }: StatusGroupProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  if (events.length === 0) return null

  return (
    <div style={{ marginBottom: '4px' }}>
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          padding: '4px 0',
          userSelect: 'none'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '10px', color: '#666' }}>
            {isExpanded ? <DownOutlined /> : <RightOutlined />}
          </span>
          <span style={{ fontSize: '11px', fontWeight: 600, color: '#333' }}>{title}</span>
        </div>
        <span style={{
          color: '#333',
          border: '1px solid #bfbfbf',
          borderRadius: '8px',
          padding: '1px 6px',
          fontSize: '10px',
          fontWeight: 600
        }}>{events.length}</span>
      </div>
      {isExpanded && (
        <div style={{ paddingLeft: '16px', paddingBottom: '4px' }}>
          {events.map(event => renderEvent(event))}
        </div>
      )}
    </div>
  )
}

type CrewLeadColumnProps = {
  crewLeadName: string | null | undefined
  crewLeadAvatar: string | null | undefined
  crewLeadColor: string | null | undefined
  events: ClusteredEvent[]
  isFirst: boolean
  isLast: boolean
}

const CrewLeadColumn = ({ crewLeadName, crewLeadAvatar, crewLeadColor, events, isFirst, isLast }: CrewLeadColumnProps) => {
  const clockedInEvents = events.filter(e => e.status === 'clocked-in')
  const plannedEvents = events.filter(e => e.status === 'planned')
  const visitedEvents = events.filter(e => e.status === 'visited')

  const hasClocked = clockedInEvents.length > 0
  const hasVisited = visitedEvents.length > 0

  const renderStatusTag = (status: number | null) => {
    if (status === ProjectStatuses.Scheduled) {
      return (
        <Tag
          style={{
            backgroundColor: '#fff7e6',
            borderColor: '#ffd591',
            color: '#fa8c16',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            margin: 0,
            fontSize: '10px',
            padding: '0 4px',
            lineHeight: '18px'
          }}
        >
          <FontAwesomeIcon icon={faTrafficCone} style={{ fontSize: '8px' }} />
          In Progress
        </Tag>
      )
    }

    if (status === ProjectStatuses.Completed) {
      return (
        <Tag
          style={{
            backgroundColor: '#f6ffed',
            borderColor: '#b7eb8f',
            color: '#52c41a',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            margin: 0,
            fontSize: '10px',
            padding: '0 4px',
            lineHeight: '18px'
          }}
        >
          <CheckCircleOutlined style={{ fontSize: '8px' }} />
          Completed
        </Tag>
      )
    }

    return null
  }

  const renderEvent = (event: ClusteredEvent) => (
    <div
      key={event.id}
      style={{ marginBottom: '8px' }}
    >
      <div style={{ fontSize: '12px', color: '#000', fontWeight: 500 }}>
        {event.eventName}
      </div>
      <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
        {event.customerDisplayName}
      </div>
      <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
        {dayjs(event.start).format('h:mm A')} - {dayjs(event.end).format('h:mm A')}
      </div>
      <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {event.workAreas.length > 0 ? (
          event.workAreas.map(wa => (
            <div
              key={wa.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '10px'
              }}
            >
              <span style={{ color: '#666' }}>{wa.name}</span>
              {renderStatusTag(wa.status)}
            </div>
          ))
        ) : (
          <div style={{ fontSize: '10px', color: '#999' }}>No Assigned Work Areas</div>
        )}
      </div>
    </div>
  )

  return (
    <div
      style={{
        flex: 1,
        minWidth: '200px',
        paddingLeft: isFirst ? 0 : '12px',
        paddingRight: isLast ? 0 : '12px',
        borderLeft: isFirst ? 'none' : '1px solid #f0f0f0'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '10px',
        paddingBottom: '8px',
        borderBottom: `2px solid ${crewLeadColor || '#ccc'}`
      }}>
        <Avatar
          src={crewLeadAvatar}
          size={24}
          style={{
            backgroundColor: '#f0f0f0',
            color: '#000',
            fontSize: '11px',
            fontWeight: 600
          }}
        >
          {crewLeadName ? getInitials(crewLeadName) : '?'}
        </Avatar>
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>
          {crewLeadName || 'Unassigned'}
        </span>
      </div>
      {hasClocked ? (
        <>
          <StatusGroup title="Clocked In" events={clockedInEvents} defaultExpanded={true} renderEvent={renderEvent} />
          <StatusGroup title="Planned" events={plannedEvents} defaultExpanded={false} renderEvent={renderEvent} />
          <StatusGroup title="Visited" events={visitedEvents} defaultExpanded={false} renderEvent={renderEvent} />
        </>
      ) : hasVisited ? (
        <>
          <StatusGroup title="Visited" events={visitedEvents} defaultExpanded={true} renderEvent={renderEvent} />
          <StatusGroup title="Planned" events={plannedEvents} defaultExpanded={false} renderEvent={renderEvent} />
        </>
      ) : (
        <StatusGroup title="Planned" events={plannedEvents} defaultExpanded={true} renderEvent={renderEvent} />
      )}
    </div>
  )
}

const ScheduleMapClusterMarker = ({
  latitude,
  longitude,
  events,
  address,
  isHovered = false,
  showPopupOnHover = false,
  mapRef,
  onMouseEnter,
  onMouseLeave
}: Props) => {
  const [popupVisible, setPopupVisible] = useState(false)

  const uniqueCrewLeads = useMemo(() => {
    const seen = new Set<string>()
    return events
      .filter(e => {
        if (!e.crewLeadName) return false
        const key = e.crewLeadName
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      .map(e => ({
        name: e.crewLeadName!,
        avatar: e.crewLeadAvatar,
        color: e.crewLeadColor || DEFAULT_CLUSTER_COLOR
      }))
  }, [events])

  const borderGradient = useMemo(() => {
    const colors = uniqueCrewLeads.map(cl => cl.color)
    if (colors.length === 0) return DEFAULT_CLUSTER_COLOR
    if (colors.length === 1) return colors[0]
    const segmentSize = 360 / colors.length
    const stops = colors.map((color, i) => {
      const start = i * segmentSize
      const end = (i + 1) * segmentSize
      return `${color} ${start}deg ${end}deg`
    })
    return `conic-gradient(from -90deg, ${stops.join(', ')})`
  }, [uniqueCrewLeads])

  const getPositioning = () => {
    if (!mapRef?.current) {
      return { anchor: 'bottom' as const, offset: { bottom: [0, -95] as [number, number] } }
    }

    const point = mapRef.current.project([longitude, latitude])
    const container = mapRef.current.getContainer()
    const mapWidth = container.clientWidth

    const popupWidth = 320
    const popupHeight = 200
    const markerHeight = 70
    const isNearLeft = point.x < popupWidth
    const isNearRight = point.x > mapWidth - popupWidth
    const isNearTop = point.y < popupHeight + markerHeight

    if (isNearTop) {
      if (isNearLeft) {
        return { anchor: 'top-left' as const, offset: { 'top-left': [15, 10] as [number, number] } }
      }
      if (isNearRight) {
        return { anchor: 'top-right' as const, offset: { 'top-right': [-15, 10] as [number, number] } }
      }
      return { anchor: 'top' as const, offset: { top: [0, 10] as [number, number] } }
    }

    if (isNearLeft) {
      return { anchor: 'left' as const, offset: { left: [15, -30] as [number, number] } }
    }
    if (isNearRight) {
      return { anchor: 'right' as const, offset: { right: [-15, -30] as [number, number] } }
    }

    return { anchor: 'bottom' as const, offset: { bottom: [0, -95] as [number, number] } }
  }

  const { anchor, offset } = getPositioning()

  const isSingleColor = uniqueCrewLeads.length <= 1
  const borderColor = isSingleColor ? (uniqueCrewLeads[0]?.color || DEFAULT_CLUSTER_COLOR) : undefined

  const markerStyle = {
    position: 'relative',
    width: '50px',
    height: '70px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: 'pointer'
  } as const

  const pinHeadOuterStyle = {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    background: isSingleColor ? borderColor : borderGradient,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
    position: 'relative',
    boxShadow: isHovered ? '0 0 16px 6px rgba(0,0,0,0.2)' : 'none',
    transition: 'box-shadow 0.2s, transform 0.2s',
    transform: isHovered ? 'scale(1.15)' : 'scale(1)',
  } as const

  const pinHeadInnerStyle = {
    width: '42px',
    height: '42px',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  } as const

  const pinTailStyle = {
    width: '30px',
    height: '30px',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    clipPath: 'polygon(50% 100%, 0 0, 100% 0)',
    marginTop: '-6px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
    zIndex: 1
  }

  const hasClockedIn = events.some(e => e.status === 'clocked-in')
  const hasVisited = events.some(e => e.status === 'visited')

  const getStatusBadgeColor = () => {
    if (hasClockedIn) return '#52c41a'
    if (hasVisited) return '#8c8c8c'
    return '#1890ff'
  }

  const statusBadgeStyle = {
    position: 'absolute',
    top: '2px',
    right: '2px',
    width: '14px',
    height: '14px',
    backgroundColor: getStatusBadgeColor(),
    borderRadius: '50%',
    border: '2px solid white',
    zIndex: 10,
    animation: hasClockedIn ? 'pulse 2s infinite' : 'none'
  } as const

  const eventsByCrewLead = useMemo(() => {
    const grouped = events.reduce((acc, event) => {
      const key = event.crewLeadName || 'Unassigned'
      if (!acc[key]) {
        acc[key] = {
          crewLeadName: event.crewLeadName,
          crewLeadAvatar: event.crewLeadAvatar,
          crewLeadColor: event.crewLeadColor,
          events: []
        }
      }
      acc[key].events.push(event)
      return acc
    }, {} as Record<string, { crewLeadName: string | null | undefined, crewLeadAvatar: string | null | undefined, crewLeadColor: string | null | undefined, events: ClusteredEvent[] }>)
    return Object.values(grouped)
  }, [events])

  const tooltipContent = (
    <div style={{ padding: '4px' }}>
      {address && (
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' }}>
          {address}
        </div>
      )}
      <div style={{ display: 'flex', gap: '0' }}>
        {eventsByCrewLead.map((group, groupIndex) => (
          <CrewLeadColumn
            key={group.crewLeadName || 'unassigned'}
            crewLeadName={group.crewLeadName}
            crewLeadAvatar={group.crewLeadAvatar}
            crewLeadColor={group.crewLeadColor}
            events={group.events}
            isFirst={groupIndex === 0}
            isLast={groupIndex === eventsByCrewLead.length - 1}
          />
        ))}
      </div>
    </div>
  )

  return (
    <>
      <Marker
        latitude={latitude}
        longitude={longitude}
        anchor="bottom"
      >
        <style>{`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.2); opacity: 0.7; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}</style>
        <div
          style={markerStyle}
          onClick={() => setPopupVisible(!popupVisible)}
          onMouseEnter={() => onMouseEnter?.()}
          onMouseLeave={() => { if (!popupVisible) onMouseLeave?.() }}
        >
          <div style={statusBadgeStyle} />
          <div style={pinHeadOuterStyle}>
            <div style={pinHeadInnerStyle}>
              {uniqueCrewLeads.length === 0 ? (
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: DEFAULT_CLUSTER_COLOR,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#fff'
                }}>
                  {events.length}
                </div>
              ) : uniqueCrewLeads.length === 1 ? (
                uniqueCrewLeads[0].avatar ? (
                  <Avatar
                    src={uniqueCrewLeads[0].avatar}
                    size={28}
                    style={{
                      border: 'none',
                      fontSize: '12px',
                      fontWeight: 600
                    }}
                  >
                    {getInitials(uniqueCrewLeads[0].name)}
                  </Avatar>
                ) : (
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: uniqueCrewLeads[0].color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#fff'
                  }}>
                    {getInitials(uniqueCrewLeads[0].name)}
                  </div>
                )
              ) : (
                <Avatar.Group
                  size={24}
                >
                  {uniqueCrewLeads.map((cl, i) => (
                    <Avatar
                      key={i}
                      src={cl.avatar}
                      size={24}
                      style={{
                        backgroundColor: cl.color,
                        color: '#fff',
                        fontSize: '11px',
                        fontWeight: 600,
                        border: `2px solid ${cl.color}`
                      }}
                    >
                      {getInitials(cl.name)}
                    </Avatar>
                  ))}
                </Avatar.Group>
              )}
            </div>
          </div>
          <div style={pinTailStyle} />
        </div>
      </Marker>
      {(popupVisible || showPopupOnHover) && (
        <Popup
          latitude={latitude}
          longitude={longitude}
          anchor={anchor}
          onClose={() => setPopupVisible(false)}
          closeButton={false}
          closeOnClick={true}
          offset={offset}
          maxWidth="none"
          style={{ zIndex: 10 }}
          className="cluster-marker-popup"
        >
          <style>{`.cluster-marker-popup .mapboxgl-popup-tip { display: none; } .cluster-marker-popup .mapboxgl-popup-content { max-width: none; }`}</style>
          {tooltipContent}
        </Popup>
      )}
    </>
  )
}

export default ScheduleMapClusterMarker
